import {
  Badge,
  Button,
  EmptyState,
  PageHeader,
  ResponsiveList,
  Tab as TabComponent,
  TabList,
  TabPanel,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
  Tabs,
  formatDateTime,
  useToast,
} from '@catering/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell } from 'lucide-react';
import { type JSX, useState } from 'react';

import { hasPermission } from '../../auth/has-permission';
import { useCurrentUser } from '../../auth/use-current-user';
import {
  fetchFailedNotifications,
  fetchNotifications,
  markNotificationRead,
  retryOutboxNotification,
} from '../../notifications/api';
import { NOTIFICATION_EVENT_TYPE_LABELS } from '../../notifications/event-type-labels';

const PAGE_SIZE = 20;

type Tab = 'own' | 'failed';

function plainText(body: string): string {
  return body.replace(/<[^>]+>/g, '');
}

function eventTypeLabel(eventType: string): string {
  return NOTIFICATION_EVENT_TYPE_LABELS[eventType] ?? eventType;
}

/**
 * `in-app-notifications/spec.md`: own notifications (paginated,
 * mark-as-read) plus the `notification.manage`-gated FAILED view with
 * manual retry, in one screen with a tab switch — the backend already
 * enforces the permission (a `CLIENT_*`/unprivileged user gets a 403 on
 * the `failed` tab's request); the tab itself only shows to people who
 * hold the permission, so nobody ends on a "Sin acceso" screen).
 */
export function NotificationListPage(): JSX.Element {
  const [tab, setTab] = useState<Tab>('own');
  const [offset, setOffset] = useState(0);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const currentUser = useCurrentUser();
  const canManage = hasPermission(currentUser.data, 'notification.manage');

  const own = useQuery({
    queryKey: ['notifications', 'list', offset],
    queryFn: () => fetchNotifications(PAGE_SIZE, offset),
    enabled: tab === 'own',
  });

  const failed = useQuery({
    queryKey: ['notifications', 'failed', offset],
    queryFn: () => fetchFailedNotifications(PAGE_SIZE, offset),
    enabled: tab === 'failed',
  });

  const markRead = useMutation({
    mutationFn: markNotificationRead,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const retry = useMutation({
    mutationFn: retryOutboxNotification,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['notifications', 'failed'] });
      toast('success', 'Notificación reencolada para reintento');
    },
    onError: () => {
      toast('error', 'No se pudo reintentar la notificación');
    },
  });

  function switchTab(next: Tab): void {
    setTab(next);
    setOffset(0);
  }

  return (
    <section className="flex flex-col gap-6">
      <PageHeader
        icon={Bell}
        title="Notificaciones"
        description="Tus avisos y, si tenés el permiso, los avisos que no se pudieron enviar."
      />

      <Tabs value={tab} onValueChange={(value) => switchTab(value as Tab)}>
        <TabList aria-label="Pestañas de notificaciones">
          <TabComponent value="own">Mis notificaciones</TabComponent>
          {canManage ? <TabComponent value="failed">Fallidas</TabComponent> : null}
        </TabList>

        <TabPanel value="own">
          {own.data === undefined ? (
            <p className="text-sm text-slate-500">Cargando...</p>
          ) : own.data.items.length === 0 ? (
            <EmptyState icon={Bell} title="No tenés notificaciones" />
          ) : (
            <ResponsiveList
              items={own.data.items}
              rowKey={(item) => item.id}
              caption="Mis notificaciones"
              emptyState={<EmptyState icon={Bell} title="No tenés notificaciones" />}
              columns={[
                { key: 'body', header: 'Notificación', render: (item) => plainText(item.body) },
                {
                  key: 'createdAt',
                  header: 'Fecha',
                  render: (item) => formatDateTime(item.createdAt),
                },
                {
                  key: 'status',
                  header: 'Estado',
                  render: (item) => (
                    <Badge variant={item.readAt === null ? 'info' : 'neutral'}>
                      {item.readAt === null ? 'No leída' : 'Leída'}
                    </Badge>
                  ),
                },
              ]}
              renderCardHeader={(item) => plainText(item.body)}
              renderCardBody={(item) => (
                <>
                  <span>{formatDateTime(item.createdAt)}</span>
                  <Badge variant={item.readAt === null ? 'info' : 'neutral'}>
                    {item.readAt === null ? 'No leída' : 'Leída'}
                  </Badge>
                </>
              )}
              renderActions={(item) =>
                item.readAt === null ? (
                  <Button
                    variant="secondary"
                    onClick={() => markRead.mutate(item.id)}
                    loading={markRead.isPending && markRead.variables === item.id}
                  >
                    Marcar leída
                  </Button>
                ) : null
              }
            />
          )}

          {own.data !== undefined && own.data.total > PAGE_SIZE ? (
            <div className="mt-4 flex items-center justify-end gap-3 text-sm text-slate-600">
              <Button
                variant="secondary"
                size="sm"
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              >
                Anterior
              </Button>
              <span>
                Página {offset / PAGE_SIZE + 1} de {Math.ceil(own.data.total / PAGE_SIZE)}
              </span>
              <Button
                variant="secondary"
                size="sm"
                disabled={offset + PAGE_SIZE >= own.data.total}
                onClick={() => setOffset(offset + PAGE_SIZE)}
              >
                Siguiente
              </Button>
            </div>
          ) : null}
        </TabPanel>

        <TabPanel value="failed">
          {failed.isError ? (
            <EmptyState
              icon={Bell}
              title="Sin acceso"
              description="No tenés el permiso para ver las notificaciones fallidas."
            />
          ) : failed.data === undefined ? (
            <p className="text-sm text-slate-500">Cargando...</p>
          ) : failed.data.items.length === 0 ? (
            <EmptyState icon={Bell} title="No hay notificaciones fallidas" />
          ) : (
            <Table>
              <thead>
                <TableRow>
                  <TableHeader>Evento</TableHeader>
                  <TableHeader>Error</TableHeader>
                  <TableHeader>Intentos</TableHeader>
                  <TableHeader>&nbsp;</TableHeader>
                </TableRow>
              </thead>
              <TableBody>
                {failed.data.items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>{eventTypeLabel(item.eventType)}</TableCell>
                    <TableCell className="max-w-xs truncate">{item.errorDetail}</TableCell>
                    <TableCell>{item.attempts}</TableCell>
                    <TableCell>
                      <Button
                        variant="secondary"
                        onClick={() => retry.mutate(item.outboxId)}
                        loading={retry.isPending && retry.variables === item.outboxId}
                      >
                        Reintentar
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TabPanel>
      </Tabs>
    </section>
  );
}
